import { useEffect, useState, useMemo, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { ViewPdfLink } from "@/components/ViewPdfLink";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Wallet, FileText, CheckCircle2, Download, Send, Flag, Mail } from "lucide-react";
import { formatCurrencyBE } from "@/lib/currency";
import RemindersWidget from "@/components/reminders/RemindersWidget";
import CreditNotesBlock from "@/components/invoice/CreditNotesBlock";
import PeppolBadge from "@/components/invoice/PeppolBadge";
// Lot P0 #1 (2026-09-09) — Bandeau + disable boutons émission facture si
// coordonnées légales BE (VAT + IBAN) manquantes ou invalides.
import InvoiceReadinessBanner from "@/components/company/InvoiceReadinessBanner";
import { hasRole } from "@/lib/roles";

const STATUS_META = {
  // Phase Semi-Auto Facture (12/09/2026)
  a_envoyer: { label: "À envoyer", cls: "badge-info" },
  envoyee: { label: "Envoyée", cls: "badge-warning" },
  overdue: { label: "En retard", cls: "badge-error" },
  paid: { label: "Reçue", cls: "badge-success" },
};

// Phase 2C.M+ (2026-09-06) + Phase Semi-Auto (12/09/2026) — Matrice statut × action.
// `send` (premier envoi manuel) UNIQUEMENT sur a_envoyer. `resend` (renvoi) sur envoyee+overdue.
const INVOICE_ACTION_VIS = {
  a_envoyer: {
    pdf: { on: true },
    send: { on: true },
    markPaid: { on: false, hide: true },  // pas encore envoyée = pas encore payable
    resend: { on: false, hide: true },
  },
  envoyee: {
    pdf: { on: true },
    send: { on: false, hide: true },
    markPaid: { on: true },
    resend: { on: true },
  },
  overdue: {
    pdf: { on: true },
    send: { on: false, hide: true },
    markPaid: { on: true },
    resend: { on: true, tip: "Facture en retard — le client verra l'échéance dépassée." },
  },
  paid: {
    pdf: { on: true },
    send: { on: false, hide: true },
    markPaid: { on: false, hide: true },
    resend: { on: false, hide: true },
  },
};

const TYPE_LABEL = { acompte: "Acompte", solde: "Solde" };

/**
 * Section Facturation de la fiche chantier.
 * Affiche les factures + totaux + actions (mark-paid, émettre acompte manuel si signature sans facture, finalize).
 */
export default function ChantierInvoicesSection({ chantier, quotes = [], onChanged, currentUser }) {
  const [invoices, setInvoices] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [markingId, setMarkingId] = useState(null);
  // Phase 2C.M+ — Dialog renvoi facture (email éditable ponctuellement)
  const [resendState, setResendState] = useState({ open: false, invoice: null, email: "", busy: false });
  const [markPaymentRef, setMarkPaymentRef] = useState("");
  // Phase 9bis-hub-proactif : opt-in email confirmation réception au client (coché par défaut)
  const [markNotifyClient, setMarkNotifyClient] = useState(true);
  const [emitting, setEmitting] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [showFinalize, setShowFinalize] = useState(false);
  const [soldePreview, setSoldePreview] = useState(null);
  // Phase Avenants-α-2 (2026-09-11) — État de la modale de décision "avenants
  // envoyés non signés" (déclenchée uniquement en réponse à un 409 backend
  // AMENDMENTS_PENDING_DECISION). Le frontend n'invente PAS la présence
  // d'avenants pending : il attend le payload backend.
  const [amendmentDecision, setAmendmentDecision] = useState({
    open: false,
    pending: [],  // [{id, number, total_tvac}]
    busy: false,
  });
  // BLOC 2026-10-18 20h55 — pré-fetch href PDF via `<ViewPdfLink>` iPhone-safe.
  // Lot P0 #1 (2026-09-09) — Readiness légale BE (VAT + IBAN). Si `ready=false`,
  // disable les boutons émission facture avec title explicite. Fail-open par
  // défaut (voir InvoiceReadinessBanner) : si l'endpoint tombe, on n'empêche
  // pas l'UI — le blocage backend reste la source de vérité (422).
  const [readiness, setReadiness] = useState({ ready: true, missing: [], invalid: [], warnings: [] });

  const chantierId = chantier?.id;

  // Devis signé sur ce chantier (le plus récent)
  const signedQuote = useMemo(
    () => (quotes || []).filter((q) => q.status === "signe").sort(
      (a, b) => (b.signed_at || "").localeCompare(a.signed_at || "")
    )[0],
    [quotes]
  );

  const load = useCallback(async () => {
    if (!chantierId) return;
    setLoading(true);
    try {
      const [{ data: invs }, { data: cs }] = await Promise.all([
        api.get(`/invoices?chantier_id=${chantierId}`),
        api.get("/company/settings").catch(() => ({ data: null })),
      ]);
      setInvoices(invs);
      setCompanySettings(cs);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [chantierId]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = useMemo(() => {
    const emitted = invoices.reduce((s, i) => s + Number(i.amount_tvac || 0), 0);
    const paid = invoices
      .filter((i) => i.status === "paid")
      .reduce((s, i) => s + Number(i.amount_tvac || 0), 0);
    const totalSigned = Number(signedQuote?.total_tvac || 0);
    return {
      emitted,
      paid,
      remaining_to_emit: Math.max(0, totalSigned - emitted),
      remaining_to_pay: emitted - paid,
      total_signed: totalSigned,
    };
  }, [invoices, signedQuote]);

  const hasAcompte = invoices.some((i) => i.type === "acompte");
  const hasSolde = invoices.some((i) => i.type === "solde");
  // Phase B rôle gestionnaire_chantier (2026-09-10) — Émission facture client
  // INTERDITE (matrice). Masque les 2 boutons `emit-acompte-btn` et
  // `finalize-chantier-btn` + le bouton "créer NC" plus bas.
  const isGestionnaireChantier = hasRole(currentUser, ["gestionnaire_chantier"]);
  const canEmitInvoice = !isGestionnaireChantier;
  const showEmitAcompteButton = canEmitInvoice && !!signedQuote && !hasAcompte;
  const canFinalize = canEmitInvoice && !!signedQuote && !hasSolde && chantier?.status !== "termine";
  // Lot P0 #1 (2026-09-09) — Blocage émission facture si company pas prête.
  // Applique aux 2 boutons (`emit-acompte-btn` + `finalize-chantier-btn`) qui
  // déclenchent un `POST /quotes/{id}/generate-invoice` (refusé 422 backend).
  const readinessBlocks = !readiness.ready;
  const readinessTitle = readinessBlocks
    ? `Émission facture bloquée : ${[...readiness.missing, ...readiness.invalid].join(", ") || "coordonnées entreprise incomplètes"}. Complétez vos infos dans Paramètres > Entreprise.`
    : undefined;

  const doMarkPaid = async (invoiceId) => {
    setMarkingId(invoiceId);
    try {
      // Query param notify_client transmet le choix utilisateur au backend
      const qs = markNotifyClient ? "?notify_client=true" : "";
      await api.post(`/invoices/${invoiceId}/mark-paid${qs}`, {
        payment_reference: markPaymentRef.trim() || undefined,
      });
      toast.success(
        markNotifyClient
          ? "Facture marquée reçue et client prévenu."
          : "Facture marquée reçue."
      );
      setMarkingId(null);
      setMarkPaymentRef("");
      setMarkNotifyClient(true);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      setMarkingId(null);
    }
  };

  const emitAcompteManual = async () => {
    setEmitting(true);
    try {
      const { data } = await api.post(`/quotes/${signedQuote.id}/generate-invoice`, {
        type: "acompte",
      });
      toast.success(`Facture ${data.invoice.number} émise`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEmitting(false);
    }
  };

  const openFinalize = () => {
    // Preview du montant solde = total signé - acomptes émis
    const acomptesEmitted = invoices
      .filter((i) => i.type === "acompte")
      .reduce((s, i) => s + Number(i.amount_tvac || 0), 0);
    const solde = Number(signedQuote?.total_tvac || 0) - acomptesEmitted;
    setSoldePreview(Math.max(0, Math.round(solde * 100) / 100));
    setShowFinalize(true);
  };

  const doFinalize = async (decision = null) => {
    setFinalizing(true);
    try {
      const body = decision ? { pending_amendments_decision: decision } : undefined;
      const { data } = await api.post(`/chantiers/${chantierId}/finalize`, body);
      toast.success(`Chantier finalisé — facture ${data.invoice.number} émise`);
      setShowFinalize(false);
      setAmendmentDecision({ open: false, pending: [], busy: false });
      onChanged?.();
      load();
    } catch (e) {
      // Phase Avenants-α-2 — 409 AMENDMENTS_PENDING_DECISION → ouvre modale
      // au lieu d'afficher un toast d'erreur. Le frontend REFLÈTE le backend :
      // il n'infère pas la présence d'avenants pending, il attend le payload.
      const detail = e?.response?.data?.detail;
      if (
        e?.response?.status === 409 &&
        detail &&
        typeof detail === "object" &&
        detail.code === "AMENDMENTS_PENDING_DECISION"
      ) {
        setShowFinalize(false);
        setAmendmentDecision({
          open: true,
          pending: detail.pending || [],
          busy: false,
        });
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setFinalizing(false);
    }
  };

  const doDecideAmendments = async (decision) => {
    setAmendmentDecision((s) => ({ ...s, busy: true }));
    try {
      await doFinalize(decision);
    } finally {
      setAmendmentDecision((s) => ({ ...s, busy: false }));
    }
  };

  const downloadPdf = null; // Lot 2026-10-18 20h55 — remplacé par <ViewPdfLink> inline ci-dessous

  return (
    <Card
      className="border-slate-800 bg-slate-900/40"
      data-testid="chantier-invoices-section"
    >
      <CardContent className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-200">
            <Wallet className="h-4 w-4" /> Facturation ({invoices.length})
          </h3>
          <div className="flex gap-2">
            {showEmitAcompteButton && (
              <Button
                variant="outline"
                onClick={emitAcompteManual}
                disabled={emitting || readinessBlocks}
                title={readinessTitle}
                className="border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid={readinessBlocks ? "emit-acompte-btn-disabled" : "emit-acompte-btn"}
              >
                {emitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Émettre l&apos;acompte
              </Button>
            )}
            {canFinalize && (
              <Button
                onClick={openFinalize}
                disabled={readinessBlocks}
                title={readinessTitle}
                className="bg-[#C4553B] text-white hover:bg-[#A8402C] disabled:cursor-not-allowed disabled:opacity-50"
                data-testid={readinessBlocks ? "finalize-chantier-btn-disabled" : "finalize-chantier-btn"}
              >
                <Flag className="mr-2 h-4 w-4" /> Finaliser le chantier
              </Button>
            )}
          </div>
        </div>

        {/* Lot P0 #1 (2026-09-09) — Bandeau readiness légale BE au-dessus des
            totaux. Affiche uniquement si signedQuote présent (facturation à
            venir). Blocage front (boutons disabled) + backend (POST 422). */}
        {signedQuote && (
          <InvoiceReadinessBanner
            className="mb-4"
            onReadinessChange={setReadiness}
          />
        )}

        {signedQuote && (
          <div className="mb-4 grid grid-cols-2 gap-3 rounded border border-slate-800 bg-slate-950/30 p-3 text-sm sm:grid-cols-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Total signé</div>
              <div className="tabular font-semibold text-slate-100" data-testid="total-signed">
                {formatCurrencyBE(totals.total_signed)}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Facturé</div>
              <div className="tabular font-semibold text-slate-100" data-testid="total-emitted">
                {formatCurrencyBE(totals.emitted)}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Payé</div>
              <div className="tabular font-semibold text-slate-100" data-testid="total-paid">
                {formatCurrencyBE(totals.paid)}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Reste à payer</div>
              <div className="tabular font-semibold text-[#F0C572]" data-testid="total-remaining-pay">
                {formatCurrencyBE(totals.remaining_to_pay)}
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-6 text-center">
            <Loader2 className="mx-auto h-4 w-4 animate-spin text-slate-500" />
          </div>
        ) : invoices.length === 0 ? (
          <p className="text-sm text-slate-500">Aucune facture émise pour ce chantier.</p>
        ) : (
          <ul className="space-y-2">
            {invoices.map((inv) => {
              const meta = STATUS_META[inv.status] || STATUS_META.a_envoyer;
              return (
                <li
                  key={inv.id}
                  data-testid={`invoice-item-${inv.id}`}
                  className="rounded border border-slate-800 bg-slate-950/30 p-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <FileText className="h-4 w-4 text-slate-400" />
                        <span className="font-mono text-sm text-slate-100">{inv.number}</span>
                        <Badge className={`border ${meta.cls}`}>
                          {TYPE_LABEL[inv.type] || inv.type} · {meta.label}
                        </Badge>
                        {/* Phase Semi-Auto (12/09/2026) — badge "Signé" côté fiche chantier
                            (Q3=A+B). Cohérent avec le listing `/factures`. */}
                        <Badge
                          className="border border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                          data-testid={`invoice-signed-badge-${inv.id}`}
                          title="Cette facture correspond à un devis parent signé par le client."
                        >
                          ✓ Devis signé
                        </Badge>
                        <PeppolBadge
                          status={inv.peppol_status}
                          invoiceId={inv.id}
                          lastError={inv.peppol_last_error}
                          onRetried={load}
                        />
                      </div>
                      <div className="mt-0.5 text-xs text-slate-500">
                        Émise le {new Date(inv.issued_at).toLocaleDateString("fr-BE")} · Échéance{" "}
                        {new Date(inv.due_at).toLocaleDateString("fr-BE")}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="tabular text-sm font-semibold text-slate-100" data-testid={`invoice-amount-${inv.id}`}>
                        {formatCurrencyBE(inv.amount_tvac)}
                      </div>
                      {inv.paid_at && (
                        <div className="text-xs text-[#B8C784]">
                          Payée le {new Date(inv.paid_at).toLocaleDateString("fr-BE")}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <ViewPdfLink
                      entityType="invoice"
                      entityId={inv.id}
                      enabled={true}
                      label="PDF"
                      testid={`invoice-pdf-btn-${inv.id}`}
                      as="button-outline"
                      className="h-8 border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-800"
                    />
                    {(() => {
                      const vis = INVOICE_ACTION_VIS[inv.status] || INVOICE_ACTION_VIS.a_envoyer;
                      return (
                        <>
                          {/* Phase Semi-Auto (12/09/2026) — Premier envoi manuel */}
                          {!vis.send.hide && (
                            <Button
                              size="sm"
                              onClick={async () => {
                                try {
                                  const res = await api.post(`/invoices/${inv.id}/send`, {});
                                  toast.success(`Facture envoyée à ${res.data.email}`);
                                  load();
                                  onChanged?.();
                                } catch (err) {
                                  toast.error("Envoi impossible : " + formatApiError(err));
                                }
                              }}
                              disabled={!vis.send.on}
                              className="bg-emerald-600 text-white hover:bg-emerald-700"
                              data-testid={`invoice-send-btn-${inv.id}`}
                            >
                              <Send className="mr-1 h-3 w-3" /> Envoyer par email
                            </Button>
                          )}
                          {!vis.markPaid.hide && (
                            <Button
                              size="sm"
                              onClick={() => setMarkingId(inv.id)}
                              disabled={!vis.markPaid.on}
                              className="bg-[#7A8450] text-white hover:bg-[#5A6338]"
                              data-testid={`mark-paid-btn-${inv.id}`}
                              title={vis.markPaid.on ? undefined : vis.markPaid.tip}
                            >
                              <CheckCircle2 className="mr-1 h-3 w-3" /> Marquer reçue
                            </Button>
                          )}
                          {!vis.resend.hide && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setResendState({
                                open: true, invoice: inv,
                                email: inv.client_snapshot?.email || "", busy: false,
                              })}
                              disabled={!vis.resend.on}
                              className="border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-800"
                              data-testid={`invoice-resend-btn-${inv.id}`}
                              title={vis.resend.on ? vis.resend.tip : (vis.resend.tip || "")}
                            >
                              <Mail className="mr-1 h-3 w-3" /> Renvoyer
                            </Button>
                          )}
                        </>
                      );
                    })()}
                    <span className="ml-auto font-mono text-xs text-slate-500" data-testid={`invoice-comm-${inv.id}`}>
                      {inv.structured_communication}
                    </span>
                  </div>
                  {/* Phase 7a — Widget Relances IA facture (visible même feature off pour transparence) */}
                  {inv.status !== "paid" && (
                    <div className="mt-3">
                      <RemindersWidget
                        targetType="invoice"
                        targetId={inv.id}
                        featureGloballyDisabled={!companySettings?.invoice_reminders_enabled}
                      />
                    </div>
                  )}
                  {/* Phase 7b — Notes de crédit */}
                  <CreditNotesBlock invoice={inv} onCredited={load} />
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      {/* Modal Mark-Paid */}
      <Dialog open={!!markingId && markingId !== "__done__"} onOpenChange={(o) => !o && setMarkingId(null)}>
        <DialogContent className="border-slate-800 bg-slate-900" data-testid="mark-paid-modal">
          <DialogHeader>
            <DialogTitle>Marquer la facture comme reçue</DialogTitle>
            <DialogDescription className="text-slate-400">
              Confirmer la réception du paiement. La date par défaut est aujourd&apos;hui.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-slate-300" htmlFor="payment-ref">
              Référence du virement (optionnel)
            </Label>
            <Input
              id="payment-ref"
              value={markPaymentRef}
              onChange={(e) => setMarkPaymentRef(e.target.value)}
              placeholder="Ex : Virement Belfius du 12/03"
              className="border-slate-700 bg-slate-950/40 text-slate-100"
              data-testid="payment-ref-input"
            />
            {/* Phase 9bis-hub-proactif — Prompt notify client sur mark-paid */}
            <div className="flex items-start gap-3 rounded border border-slate-800 bg-slate-950/30 p-3">
              <Checkbox
                id="mark-paid-notify"
                checked={markNotifyClient}
                onCheckedChange={(v) => setMarkNotifyClient(!!v)}
                className="mt-0.5 border-slate-600 data-[state=checked]:bg-[#7A8450]"
                data-testid="mark-paid-notify-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="mark-paid-notify" className="cursor-pointer text-sm text-slate-200">
                  Prévenir le client de la réception par email
                </Label>
                <p className="mt-0.5 text-xs text-slate-500">
                  Un email chaleureux confirmera la bonne réception du paiement.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMarkingId(null)}
              className="border-slate-700 bg-slate-800/60 text-slate-200"
            >
              Annuler
            </Button>
            <Button
              onClick={() => doMarkPaid(markingId)}
              className="bg-[#7A8450] text-white hover:bg-[#5A6338]"
              data-testid="mark-paid-confirm-btn"
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Finalize */}
      <Dialog open={showFinalize} onOpenChange={setShowFinalize}>
        <DialogContent className="border-slate-800 bg-slate-900" data-testid="finalize-modal">
          <DialogHeader>
            <DialogTitle>Finaliser le chantier</DialogTitle>
            <DialogDescription className="text-slate-400">
              Cette action émet la facture de solde et passe le chantier au statut « Finalisé ». Action irréversible.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded border border-slate-800 bg-slate-950/30 p-4 text-center">
            <div className="text-xs uppercase tracking-wide text-slate-500">
              Facture de solde à émettre
            </div>
            <div className="tabular mt-1 text-2xl font-semibold text-[#C4553B]" data-testid="finalize-solde-preview">
              {formatCurrencyBE(soldePreview || 0)}
            </div>
            <div className="mt-1 text-xs text-slate-500">TVAC</div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowFinalize(false)}
              className="border-slate-700 bg-slate-800/60 text-slate-200"
            >
              Annuler
            </Button>
            <Button
              onClick={() => doFinalize()}
              disabled={finalizing}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
              data-testid="finalize-confirm-btn"
            >
              {finalizing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Générer la facture solde
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase Avenants-α-2 (2026-09-11) — Modale de décision "avenants
          envoyés non signés". S'ouvre uniquement en réponse à un 409
          AMENDMENTS_PENDING_DECISION backend. Le contenu (liste + montants)
          vient EXCLUSIVEMENT du payload backend, aucun recalcul frontend. */}
      <Dialog
        open={amendmentDecision.open}
        onOpenChange={(o) => !o && !amendmentDecision.busy &&
          setAmendmentDecision({ open: false, pending: [], busy: false })}
      >
        <DialogContent
          className="border-slate-800 bg-slate-900"
          data-testid="amendment-decision-modal"
        >
          <DialogHeader>
            <DialogTitle>Avenants envoyés non signés</DialogTitle>
            <DialogDescription className="text-slate-400">
              {amendmentDecision.pending.length} avenant(s) envoyé(s) mais pas
              encore signé(s) par le client. Choisissez comment traiter ces
              montants dans la facture de solde.
            </DialogDescription>
          </DialogHeader>
          <div
            className="max-h-72 space-y-2 overflow-auto rounded border border-slate-800 bg-slate-950/30 p-3"
            data-testid="amendment-decision-list"
          >
            {amendmentDecision.pending.map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between gap-3 rounded border border-slate-800 bg-slate-950/40 p-2 text-sm"
                data-testid={`amendment-decision-row-${a.id}`}
              >
                <div className="flex items-center gap-2">
                  <Badge className="border border-[#C4553B]/50 bg-[#C4553B]/10 text-[#F0C572]">
                    Avenant
                  </Badge>
                  <span className="font-mono text-slate-100">{a.number || "—"}</span>
                </div>
                <span className="tabular whitespace-nowrap font-semibold text-slate-100">
                  {formatCurrencyBE(a.total_tvac || 0)}
                </span>
              </div>
            ))}
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              onClick={() => doDecideAmendments("exclude")}
              disabled={amendmentDecision.busy}
              className="border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-800"
              data-testid="amendment-decision-exclude-btn"
            >
              {amendmentDecision.busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Facturer sans
            </Button>
            <Button
              onClick={() => doDecideAmendments("include")}
              disabled={amendmentDecision.busy}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
              data-testid="amendment-decision-include-btn"
            >
              {amendmentDecision.busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Inclure au solde
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 2C.M+ — Dialog renvoi facture (email éditable ponctuellement) */}
      <Dialog
        open={resendState.open}
        onOpenChange={(o) => !o && setResendState((s) => ({ ...s, open: false }))}
      >
        <DialogContent
          className="border-slate-800 bg-slate-900 text-slate-100 sm:max-w-md"
          data-testid={resendState.invoice ? `invoice-resend-dialog-${resendState.invoice.id}` : "invoice-resend-dialog"}
        >
          <DialogHeader>
            <DialogTitle>Renvoyer la facture par email</DialogTitle>
            <DialogDescription className="text-slate-400">
              Facture <strong className="text-slate-200">
                {resendState.invoice?.number || "—"}
              </strong> — renvoi du PDF sans nouvelle version.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="invoice-resend-email">Adresse email destinataire</Label>
              <Input
                id="invoice-resend-email"
                type="email"
                value={resendState.email}
                onChange={(e) => setResendState((s) => ({ ...s, email: e.target.value }))}
                placeholder="client@exemple.be"
                className="mt-1 border-slate-700 bg-slate-950/50"
                data-testid="invoice-resend-email-input"
              />
              <p className="mt-1 text-xs text-slate-500">
                Modifier cette adresse ne change pas la fiche contact — c'est un renvoi ponctuel.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setResendState((s) => ({ ...s, open: false }))}
              className="text-slate-300"
              disabled={resendState.busy}
            >
              Annuler
            </Button>
            <Button
              onClick={async () => {
                setResendState((s) => ({ ...s, busy: true }));
                try {
                  const inv = resendState.invoice;
                  const body = resendState.email && resendState.email !== (inv?.client_snapshot?.email || "")
                    ? { email_override: resendState.email.trim() }
                    : {};
                  const { data } = await api.post(`/invoices/${inv.id}/resend`, body);
                  toast.success(`Facture renvoyée à ${data.sent_to}`);
                  setResendState({ open: false, invoice: null, email: "", busy: false });
                } catch (e) {
                  toast.error(formatApiError(e));
                  setResendState((s) => ({ ...s, busy: false }));
                }
              }}
              disabled={resendState.busy || !resendState.email || !resendState.email.includes("@")}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
              data-testid="invoice-resend-submit"
            >
              {resendState.busy ? "Envoi…" : "Renvoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
